import React from 'react';
import { useDefaultShopDataStack } from 'store';
import { flattenTreeData, extractApplicationPolicy } from 'utils';
import Calculate from 'utils/Calculate';
import calculateApplyPolicy from 'utils/productPolicyUtils';

const DEFAULT_GRADE_BENEFIT = {
  discount_rate: 0,
  discount_type: 'R',
  reserve_rate: 0,
  reserve_type: 'R',
};

const useGetShopProduct = (gradeId = null) => {
  // SECTION - State
  const [productList, setProductList] = React.useState([]);
  // !SECTION - State

  // 상품 전체 목록 호출
  const { products, categories, shops, policies, promotion } =
    useDefaultShopDataStack();
  // 카테고리 플랫 정제
  const flattenCategories = React.useMemo(
    () => flattenTreeData(categories),
    [categories]
  );

  // 카테고리 Map 선언
  const categoriesMap = React.useMemo(
    () =>
      new Map(
        flattenCategories.map((category) => [
          category.product_category_id,
          category,
        ])
      ),
    [flattenCategories]
  );

  // 입점업체 Map 선언
  const shopMap = React.useMemo(
    () => new Map(shops.map((shop) => [shop.shop_information_id, shop])),
    [shops]
  );

  /**
   * 렌탈 디폴트 옵션 추출
   * --
   * 재귀적으로 JSON 데이터를 순회하면서,
   * 배열 내 객체에 `default` 프로퍼티가 있는 경우 true인 항목만 남깁니다.
   */
  const extractDefaultSelections = React.useCallback((data) => {
    if (Array.isArray(data)) {
      // 배열 내에서 default: true인 첫 번째 객체 선택
      const defaultItem = data.find(
        (item) => item && typeof item === 'object' && item.default === true
      );

      if (!defaultItem) return null; // default: true가 없으면 null 반환

      // default로 선택된 객체를 재귀적으로 처리 (내부 배열 포함)
      return extractDefaultSelections({ ...defaultItem });
    } else if (data !== null && typeof data === 'object') {
      const result = {};
      for (const key in data) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
        if (key.endsWith('_description')) continue; // 설명 필드 제거

        // 배열이면 default 값이 있는 객체를 찾아 적용 (내부 배열도 재귀적으로 처리)
        if (Array.isArray(data[key])) {
          const selected = extractDefaultSelections(data[key]);
          if (selected !== null) result[key] = selected; // 선택된 값이 있다면 유지
        } else {
          result[key] = extractDefaultSelections(data[key]); // 재귀 처리
        }
      }
      return result;
    }
    return data;
  }, []);

  // 상품 정제
  const newProducts = React.useMemo(() => {
    if (!products.length) return [];
    return products.map((product) => {
      const {
        product_id,
        product_category_id,
        shop_information_id,
        product_file,
        grade_benefit_info,
        price,
        rental_information,
      } = product;

      // 적용 등급 할인율
      const gradeBenefit =
        grade_benefit_info && gradeId
          ? grade_benefit_info[gradeId] ?? DEFAULT_GRADE_BENEFIT
          : DEFAULT_GRADE_BENEFIT;

      // 썸네일 추출
      const fileMap = new Map(product_file.map((file) => [file.type, file]));
      const thumbnail = fileMap.get('THUMBNAIL');

      // 업체 추출
      const shopData = shopMap.get(shop_information_id);

      // 상품 카테고리 추출
      const categoryData = categoriesMap.get(product_category_id);

      // 적용 정책 추출
      const applyPolicy = policies.filter(
        (policy) =>
          policy.map_product_policy.some(
            (map) => map.product_id === product_id
          ) &&
          (policy.discount_rate > 0 || policy.reserve_rate > 0)
      );
      // 우선순위에 따른 적용되는 정책 추출
      const applicationPolicy = applyPolicy.length
        ? extractApplicationPolicy(applyPolicy, price, promotion)
        : null;

      // if(applicationPolicy){
      //   const a = applicationPolicy.map_product_policy.filter((i)=> product_id === 90);
      //   if(a.length !== 0){
      //     console.log(applicationPolicy,promotion)
      //   }
      // }

      const apply_grade_benefit = {
        policy_type: 'GRADE',
        ...gradeBenefit,
      };

      // 현재 진행중인 할인/적립 정책 적용
      const { apply_policy_info, final_discount_price, final_reserve_price } =
        calculateApplyPolicy(
          { price: price },
          apply_grade_benefit,
          applicationPolicy
        );

      return {
        ...product,
        thumbnail,
        shop_information: shopData,
        shop_information_name: shopData?.shop_information_name,
        product_category_key: categoryData?.key,
        product_category_name: categoryData?.product_category_name,
        apply_grade_benefit: apply_grade_benefit,
        apply_policy: applicationPolicy,
        rental_default_option: rental_information
          ? extractDefaultSelections(rental_information)
          : null,
        apply_policy_info,
        final_discount_price,
        final_reserve_price,
        final_discount_rate: Calculate.calculateDiscountRate(
          price,
          price - final_discount_price
        ),
      };
    });
  }, [
    products,
    categoriesMap,
    gradeId,
    policies,
    promotion,
    shopMap,
    extractDefaultSelections,
  ]);

  React.useEffect(() => {
    setProductList(newProducts);
  }, [newProducts]);

  return productList;
};

export default useGetShopProduct;
